import { useEffect, useId, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import { ArrowLeft, FolderX, Search } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { agentApi } from "@/services/agentApi";
import "./visaDocuments.css";

export function VisaDocumentsPage() {
  const { user } = useAuth();
  if (!user?.agentProfile)
    return (
      <div className="p-4" role="status">
        Loading applications…
      </div>
    );
  return (
    <VisaDocumentsChooser
      key={`${user.id}:${user.agentProfile.id}`}
      userId={user.id}
      agentId={user.agentProfile.id}
      main={!user.agentProfile.parentAgentId}
    />
  );
}

function VisaDocumentsChooser({
  userId,
  agentId,
  main,
}: {
  userId: string;
  agentId: string;
  main: boolean;
}) {
  const [params, setParams] = useSearchParams();
  const search = params.get("search")?.trim() || "";
  const rawPage = params.get("page") || "1";
  const validPage =
    params.getAll("page").length <= 1 &&
    params.getAll("search").length <= 1 &&
    /^[1-9]\d*$/.test(rawPage) &&
    Number.isSafeInteger(Number(rawPage));
  const page = validPage ? Number(rawPage) : 1;
  const [input, setInput] = useState(search);
  const searchId = useId();
  useEffect(() => {
    setInput(search);
  }, [search]);
  const update = (nextSearch: string, nextPage: number) => {
    const next = new URLSearchParams();
    if (nextSearch) next.set("search", nextSearch);
    if (nextPage > 1) next.set("page", String(nextPage));
    setParams(next);
  };
  const query = useQuery(
    ["visa-document-applications", userId, agentId, main, search, page],
    async ({ signal }) => {
      const result = await agentApi.getApplications(
        {
          page,
          limit: 10,
          search: search || undefined,
          includeSubAccounts: main,
          sortBy: "updatedAt",
          sortOrder: "desc",
        },
        signal,
      );
      const validTotals =
        Number.isSafeInteger(result.total) && result.total >= 0;
      const expectedPages = Math.ceil(result.total / 10);
      // Existing API variants represent an empty list as either zero or one page.
      const validPages =
        Number.isSafeInteger(result.pages) &&
        (result.pages === expectedPages ||
          (result.total === 0 && result.pages === 1));
      const expectedRows = Math.min(
        10,
        Math.max(0, result.total - (page - 1) * 10),
      );
      const inRange = page <= Math.max(1, result.pages);
      const validRows =
        Array.isArray(result.applications) &&
        result.applications.length <= 10 &&
        (!inRange || result.applications.length === expectedRows);
      if (!validTotals || !validPages || !validRows)
        throw new Error("Invalid application results");
      return result;
    },
    { enabled: validPage, retry: false, keepPreviousData: false, cacheTime: 0 },
  );
  const data = query.data;
  const pages = Math.max(1, data?.pages || 1);
  const outOfRange = Boolean(data && page > pages);
  return (
    <div className="container-fluid py-4 visa-chooser">
      <section
        className="visa-chooser__panel"
        aria-label="Choose an application"
      >
        <header className="visa-chooser__intro">
          <Link to="/dashboard" className="visa-chooser__back">
            <ArrowLeft size={12} aria-hidden="true" />
            Back to dashboard
          </Link>
          <h1>Visa Documents</h1>
          <p>
            <span className="visa-chooser__desktop-description">
              Choose an application to open its Visa Support workspace.
            </span>
            <span className="visa-chooser__mobile-description">
              Select an application
            </span>
          </p>
          <form
            className="visa-chooser__search"
            onSubmit={(event) => {
              event.preventDefault();
              update(input.trim(), 1);
            }}
          >
            <label htmlFor={searchId}>Search by student or reference</label>
            <div className="visa-chooser__search-controls">
              <div className="visa-chooser__search-field">
                <Search size={16} aria-hidden="true" />
                <input
                  id={searchId}
                  type="search"
                  placeholder="Search by student or reference"
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                />
              </div>
              <button
                type="submit"
                className="visa-chooser__button visa-chooser__submit"
              >
                Search
              </button>
              {search && (
                <button
                  type="button"
                  className="visa-chooser__button"
                  onClick={() => update("", 1)}
                >
                  Clear search
                </button>
              )}
            </div>
          </form>
        </header>
        {!validPage ? (
          <div className="visa-chooser__state" role="alert">
            Invalid page.{" "}
            <button
              className="visa-chooser__text-action"
              onClick={() => update(search, 1)}
            >
              Go to first page
            </button>
          </div>
        ) : query.isError ? (
          <div className="visa-chooser__state" role="alert">
            <p>Could not load applications. Please try again.</p>
            <button
              className="visa-chooser__button"
              onClick={() => void query.refetch()}
            >
              Retry
            </button>
          </div>
        ) : query.isLoading ? (
          <div className="visa-chooser__state" role="status">
            <span
              className="spinner-border spinner-border-sm me-2"
              aria-hidden="true"
            />
            Loading applications…
          </div>
        ) : outOfRange ? (
          <div className="visa-chooser__state">
            <p>This page is no longer available.</p>
            <button
              className="visa-chooser__button"
              onClick={() => update(search, 1)}
            >
              Go to first page
            </button>
          </div>
        ) : data?.applications.length === 0 ? (
          <div className="visa-chooser__state">
            <span className="visa-chooser__state-icon">
              {search ? (
                <Search size={24} aria-hidden="true" />
              ) : (
                <FolderX size={24} aria-hidden="true" />
              )}
            </span>
            <p className="visa-chooser__state-title">
              {search
                ? "No matching applications found."
                : "No applications yet."}
            </p>
            <p className="visa-chooser__state-description">
              {search
                ? "We couldn't find any applications for that search term."
                : "Start by creating your first student record."}
            </p>
            {search ? (
              <button
                className="visa-chooser__text-action"
                onClick={() => update("", 1)}
              >
                Clear search
              </button>
            ) : (
              <Link
                className="visa-chooser__text-action"
                to="/applications/new"
              >
                Create application
              </Link>
            )}
          </div>
        ) : (
          data && (
            <>
              {query.isFetching && (
                <p className="small text-muted px-4 pt-3" role="status">
                  Refreshing applications…
                </p>
              )}
              <table className="visa-chooser__table">
                <caption className="visually-hidden">
                  Applications available for Visa Support
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Student</th>
                    <th scope="col">Reference &amp; Institution</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.applications.map((app) => (
                    <tr key={app.id} className="visa-chooser__row">
                      <td className="visa-chooser__name">
                        {[app.firstName, app.middleName, app.lastName]
                          .filter(Boolean)
                          .join(" ") || "Name not provided"}
                      </td>
                      <td className="visa-chooser__metadata">
                        <span className="visa-chooser__reference">
                          {app.reference || "Reference not provided"}
                        </span>
                        <span className="visa-chooser__institution">
                          {app.universities?.name || "Institution not provided"}
                        </span>
                      </td>
                      <td className="visa-chooser__action">
                        <Link
                          to={`/applications/${app.id}/visa-support`}
                          className="visa-chooser__open"
                          aria-label={`Open Visa Support for ${app.reference || [app.firstName, app.lastName].filter(Boolean).join(" ")}`}
                        >
                          Open Visa Support
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <footer className="visa-chooser__footer">
                <span>
                  {data.total === 0
                    ? "0 applications"
                    : `Showing ${(page - 1) * 10 + 1}–${(page - 1) * 10 + data.applications.length} of ${data.total} results`}
                </span>
                <nav
                  aria-label="Application pages"
                  className="visa-chooser__pagination"
                >
                  <button
                    className="visa-chooser__button"
                    disabled={page <= 1}
                    onClick={() => update(search, page - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    Page {page} of {pages}
                  </span>
                  <button
                    className="visa-chooser__button"
                    disabled={page >= pages}
                    onClick={() => update(search, page + 1)}
                  >
                    Next
                  </button>
                </nav>
              </footer>
            </>
          )
        )}
      </section>
    </div>
  );
}
